import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { saveAs } from 'file-saver';
import { DateTime } from 'luxon';
import { ToastrService } from 'ngx-toastr';
import { interval } from 'rxjs';
import { debounce, finalize } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { AppSelectOption } from '../../../../../../../_base-shared/contracts/common.interface';
import { LaravelResourceResponse } from '../../../../../../../_base-shared/contracts/laravel-response.interface';
import { Case } from '../../../../../../../_base-shared/models/Case/Case';
import { Creditor } from '../../../../../../../_base-shared/models/Entity/Creditor';
import { CasePaymentPlan } from '../../../../../../../_base-shared/models/Payment/CasePaymentPlan';
import { FinancialOverview } from '../../../../../../../_base-shared/models/Payment/FinancialOverview';
import { PaymentMethod, PaymentMethodType } from '../../../../../../../_base-shared/models/Payment/PaymentMethod';
import { PaymentProcessorType } from '../../../../../../../_base-shared/models/Payment/PaymentProcessor';
import { PaymentTerm } from '../../../../../../../_base-shared/models/Payment/PaymentTerm';
import { Product } from '../../../../../../../_base-shared/models/Product';
import { User } from '../../../../../../../_base-shared/models/User/User';
import { Address } from '../../../../../../../_base-shared/models/Location/Address';
import {
  RequestPaymentModalComponent
} from '../../../../_shared/components/request-payment-modal/request-payment-modal.component';
import { MainGlobalEventService } from '../../../../_shared/services/main-global-event.service';
import { EmailPreviewComponent } from '../../../app-document/email-preview/email-preview.component';
import { AdminPaymentHandlerComponent } from '../../../payment/admin-payment-handler/admin-payment-handler.component';
import { FinancialOverviewService } from '../../../payment/financial-overview.service';
import { PaymentMethodService } from '../../../payment/payment-method.service';
import { PaymentService } from '../../../payment/payment.service';
import { CaseCreditorService } from '../../case-creditor.service';
import { CasePaymentPlanService } from '../../case-payment-plan.service';
import { CaseService } from '../../case.service';
import { ProductService } from '../../product.service';
import * as moment from 'moment';

@Component({
  standalone: false,
  selector:    'app-case-payment-editor',
  templateUrl: './case-payment-editor.component.html',
  styleUrls:   ['./case-payment-editor.component.scss']
})
export class CasePaymentEditorComponent implements OnInit {
  public authUser: User;
  public case: Case;
  public isLoading                            = 0;
  public submittingPaymentPlan: boolean;
  public recalculatePaymentPlan: boolean;
  public paymentPlanForm: UntypedFormGroup;
  public paymentPlanResponse: LaravelResourceResponse;
  public submittingPaymentConfiguration: boolean;
  public paymentConfigurationForm: UntypedFormGroup;
  public paymentConfigResponse: LaravelResourceResponse;
  public financialOverview: FinancialOverview;
  public phaseOnePaid: boolean; // TODO:
  public phaseTwoPaid: boolean; // TODO:
  public taxRate                              = 1.21;
  public paymentMethods: Array<PaymentMethod> = [];
  public daysInMonth: Array<AppSelectOption>  = [];
  public summaryResponse: LaravelResourceResponse;
  public upcomingInstallments: {
    plan_type: 'debt_plan' | 'additional_plans',
    installment: PaymentTerm,
    maxPayable: number
  };

  public sendLinksError             = '';
  public creditors: Array<Creditor> = [];
  public products: Array<Product>   = [];

  // Payment Plan
  public selectedProduct: Product;
  public casePaymentPlanOutdated: boolean;
  public productNotAllowed: '';

  public checkEligibility = true;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: UntypedFormBuilder,
    private changeRef: ChangeDetectorRef,
    private dialog: MatDialog,
    private toastr: ToastrService,
    private translate: TranslateService,
    private caseService: CaseService,
    private caseCreditorService: CaseCreditorService,
    private globalEventsService: MainGlobalEventService,
    private paymentMethodService: PaymentMethodService,
    private productService: ProductService,
    private casePaymentPlanService: CasePaymentPlanService,
    private financialOverviewService: FinancialOverviewService,
    private paymentService: PaymentService,
  ) {
  }

  ngOnInit(): void {
    this.globalEventsService.authUser$.subscribe(user => this.authUser = user);
    this.route.parent.paramMap.subscribe(params => {
      const caseId = +params.get('id');
      this.isLoading++;
      this.caseService.get(caseId,
        ['product.payment_plans.payment_plan_phases', 'debt_payment_plan', 'creditors', 'distribution'],
        this.checkEligibility
      )
        .pipe(finalize(() => this.isLoading--))
        .subscribe(result => {
          this.case = result.data;
          this.fetchProducts();
          this.fetchPaymentMethods();
          this.displayDistributionWarningModal();
          this.fetchUpcomingInstallments();
        });
    });

    this.financialOverviewService.financialOverview$.subscribe(overview => this.financialOverview = overview);
    this.daysInMonth = [];
    for (let i = 1; i <= 28; i++) {
      this.daysInMonth.push({label: i.toString(10), value: i});
    }
  }

  private reloadCase(caseId: number): void {
    this.caseService.get(caseId)
      .pipe(finalize(() => this.isLoading--))
      .subscribe(result => {
        this.case = result.data;
      });
  }

  public isComponentDirty(): boolean {
    return this.paymentPlanForm.dirty || this.paymentConfigurationForm.dirty;
  }

  public isComponentValid(): boolean {
    return this.paymentPlanForm.valid || this.paymentConfigurationForm.valid;
  }

  public submitComponent() {
    if (this.paymentPlanForm.dirty) {
      this.submitPaymentPlanForm(this.paymentPlanForm);
    }
    if (this.paymentConfigurationForm.dirty) {
      this.submitPaymentConfigurationForm(this.paymentConfigurationForm);
    }

    return true;
  }

  private subscribeToPaymentPlanChanges() {
    this.calculatePhaseDurations();

    this.paymentPlanForm.get('phase_one_duration').valueChanges
      .pipe(debounce(() => interval(250)))
      .subscribe(value => this.calculatePhaseOneValue());

    this.paymentPlanForm.get('phase_one_value').valueChanges
      .pipe(debounce(() => interval(250)))
      .subscribe(value => this.calculatePhaseDurations());

    this.paymentPlanForm.get('phase_one_monthly_fee').valueChanges
      .pipe(debounce(() => interval(250)))
      .subscribe(value => this.calculatePhaseDurations());

    this.paymentPlanForm.get('phase_two_value').valueChanges
      .pipe(debounce(() => interval(250)))
      .subscribe(value => this.calculatePhaseDurations());

    this.paymentPlanForm.get('phase_two_monthly_fee').valueChanges
      .pipe(debounce(() => interval(250)))
      .subscribe(value => this.calculatePhaseDurations());
  }

  public calculatePhaseOneValue() {
    const phaseOneMonthlyFee = this.paymentPlanForm.get('phase_one_monthly_fee').value;
    const phaseOneDuration   = this.paymentPlanForm.get('phase_one_duration').value;

    if ( !! phaseOneMonthlyFee && !! phaseOneDuration) {
      this.paymentPlanForm.get('phase_one_value').patchValue(phaseOneMonthlyFee * phaseOneDuration);
    }
  }

  public calculatePhaseDurations() {
    const phaseOneValue      = this.paymentPlanForm.get('phase_one_value').value;
    const phaseOneMonthlyFee = this.paymentPlanForm.get('phase_one_monthly_fee').value;
    const phaseTwoValue      = this.paymentPlanForm.get('phase_two_value').value;
    const phaseTwoMonthlyFee = this.paymentPlanForm.get('phase_two_monthly_fee').value;

    let phaseOneDuration = 0;
    let phaseTwoDuration = 0;

    if ( !! phaseOneValue && !! phaseOneMonthlyFee && phaseOneMonthlyFee > 0) {
      const errors = this.paymentPlanForm.get('phase_one_monthly_fee').errors;

      if (phaseOneValue % phaseOneMonthlyFee !== 0) {
        this.paymentPlanForm.get('phase_one_monthly_fee').setErrors({...errors, ...{uneven_modulo: true}});
      } else {
        if ( ! errors || ! Object.keys(errors).length) {
          this.paymentPlanForm.get('phase_one_monthly_fee').setErrors(null);
        } else {
          let newErrors = {};
          const keys    = Object.keys(errors);
          keys.forEach((key, index) => {
            if (key !== 'uneven_modulo') {
              newErrors[key] = errors[key];
            }
          });
          if ( ! Object.keys(newErrors).length) {
            newErrors = null;
          }

          this.paymentPlanForm.get('phase_one_monthly_fee').setErrors(newErrors);
        }
      }
      phaseOneDuration = Math.ceil(phaseOneValue / phaseOneMonthlyFee);
    }
    if (phaseTwoValue !== '' && !! phaseTwoMonthlyFee && phaseTwoMonthlyFee > 0) {
      phaseTwoDuration = Math.ceil(phaseTwoValue / phaseTwoMonthlyFee);
    }

    this.paymentPlanForm.get('phase_one_duration').patchValue(phaseOneDuration);
    this.paymentPlanForm.get('phase_two_duration').patchValue(phaseTwoDuration);
  }

  public hasError(form: UntypedFormGroup, formControlName: string) {
    return form.get(formControlName)?.touched && this.paymentPlanForm.get(formControlName)?.invalid;
  }

  public sendSepaLink(send_to) {
    this.sendLinksError = '';
    const amount        = this.case.debt_payment_plan.initial_fee;

    if ( ! this.case.id || ! amount) {
      this.sendLinksError = this.translate.instant('CASES.single.save-case-error');

      return this.toastr.error(this.translate.instant('CASES.single.save-case-error'));
    }

    const data = {
      caseId: this.case.id,
      send_to
    };
    this.caseService.sendSepaLink(data).subscribe(
      () => {
        this.toastr.success(this.translate.instant('CASES.single.send-link-success'));
      },
      err => {
        console.log(err);
        this.toastr.error(this.translate.instant('CASES.single.send-link-error'));
      }
    );
  }

  public requestPaymentDialog(paymentMethodType: PaymentMethodType, paymentProcessorType: PaymentProcessorType) {
    this.sendLinksError = '';
    const amount        = this.case.debt_payment_plan.initial_fee;

    if ( ! this.case.id || ! amount) {
      this.sendLinksError = this.translate.instant('CASES.single.save-case-error');

      return this.toastr.error(this.translate.instant('CASES.single.save-case-error'));
    }

    const dialogRef = this.dialog.open(RequestPaymentModalComponent, {
      width:        '50%',
      minHeight:    200,
      disableClose: true,
      autoFocus:    false,
      data:         {
        caseId:         this.case.id,
        paymentMethodType,
        paymentProcessorType,
        amount,
        disabledAmount: true
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        if (result.success) {
          this.toastr.success(this.translate.instant('CASES.single.request-payment-success'),
            this.translate.instant('SHARED.success'));
        } else {
          this.toastr.error(this.translate.instant('CASES.single.request-payment-error'),
            this.translate.instant('SHARED.error'));
        }
      }
    });
  }

  public sendContract($event: PointerEvent, sendTo: string): void {
    $event.preventDefault();
    const data = {
      caseId:  this.case.id,
      send_to: sendTo,
      generate_contract: 1,
      generate_mandate: 1
    };
    this.caseService.sendContract(data).subscribe(next => {
        this.toastr.success(this.translate.instant('CASES.single.send-contract-success'));
      }, error => {
        this.toastr.error(this.translate.instant('CASES.single.send-contract-error'));
        this.summaryResponse = error.error;
      }
    );
  }

  public openCardForm(type: 'charge' | 'verify'): void {
    const amount = type === 'charge' && this.case.debt_payment_plan.initial_fee > 0 ? this.case.debt_payment_plan.initial_fee : 0.01;

    if (this.isComponentDirty()) {
      this.submitComponent();
    }
    const nextTerm         = this.upcomingInstallments.installment.amount > this.upcomingInstallments.installment.amount_paid;
    const maxPayableAmount = this.upcomingInstallments.maxPayable;
    if (nextTerm) {
      const dialogRef = this.dialog.open(AdminPaymentHandlerComponent, {
        width:        '60%',
        minHeight:    200,
        disableClose: false,
        panelClass:   ['custom-mat-dialog', 'scrollable-mat-dialog'],
        autoFocus:    false,
        data:         {
          caseId:          this.case.id,
          clientRole:      'client',
          type:            'moto',
          amount,
          editableAmount:  true,
          maxPayableAmount,
          paymentPlanType: this.upcomingInstallments.plan_type,
        },
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result.reFetch) {
          this.displayDistributionWarningModal();
        }
      });
    }
  }

  private fetchUpcomingInstallments(): void {
    this.isLoading++;
    this.paymentService.paymentAmount(this.case.client.uuid, this.case.uuid).pipe(finalize(() => this.isLoading--))
      .subscribe(
        res => {
          this.upcomingInstallments = res.data;
        },
        error => console.log(error),
      );
  }

  private fetchPaymentMethods(): void {
    this.isLoading++;
    this.paymentMethodService.index().pipe(finalize(() => this.isLoading--)).subscribe(
      result => this.paymentMethods = result.data,
      error => console.log(error)
    );
  }

  private fetchProducts(): void {
    this.isLoading++;
    this.productService.index({select_all: 1}).pipe(finalize(() => this.isLoading--)).subscribe(
      result => {
        this.products        = result.data;
        this.selectedProduct = this.products.find(product => product.id === this.case.product_id);
        if ( ! this.case.debt_payment_plan) {
          this.case.debt_payment_plan = this.getDefaultPaymentPlan(this.case);
        }
        this.buildPaymentPlanForm(this.case.debt_payment_plan);
        this.buildPaymentConfigurationForm(this.case.debt_payment_plan);
      }
    );
  }

  private getDefaultPaymentPlan(clientCase: Case): CasePaymentPlan {
    const selectedPaymentPlan = clientCase.product.payment_plans.find(
      plan => plan.is_joint === clientCase.joint_application
    );
    const phaseOne            = selectedPaymentPlan.payment_plan_phases.find(phase => phase.phase_order === 1);
    const phaseTwo            = selectedPaymentPlan.payment_plan_phases.find(phase => phase.phase_order === 2);

    const paymentPlan = new CasePaymentPlan();

    paymentPlan.phase_one_value       = phaseOne.amount;
    paymentPlan.phase_one_monthly_fee = phaseOne.installment_amount;
    paymentPlan.phase_one_duration    = Math.ceil(phaseOne.amount / phaseOne.installment_amount);

    paymentPlan.phase_two_value       = phaseTwo.amount;
    paymentPlan.phase_two_monthly_fee = phaseTwo.installment_amount;
    paymentPlan.phase_two_duration    = Math.ceil(phaseTwo.amount / phaseTwo.installment_amount);

    return paymentPlan;
  }

  public getPaymentMethodSlug(paymentMethodId: number): string {
    const paymentMethod = this.paymentMethods?.find(pMethod => {
      return Number(pMethod.id) === Number(paymentMethodId);
    });

    return paymentMethod?.slug;
  }

  public contractButtonDisabled(): boolean {
    
    return ! this.paymentPlanForm.get('phase_one_monthly_fee').value ||
      // !this.paymentPlanForm.get('phase_two_monthly_fee').value ||
      ! this.paymentConfigurationForm.get('payment_day').value || ! this.validIdCards(this.case) ||
      ! this.case.product_assigned_at ||
      this.validateAddress(this.case.client?.address) ||
      this.validateUser(this.case.client) ||
      this.isAllowedProduct(this.selectedProduct);
  }

  private validateUser(user: User): boolean {
    const requiredFields = ['first_name', 'last_name', 'date_of_birth', 'employment', 'home_owner'];
    
    if(this.validateDateOfBirth(user.date_of_birth)){
      return true;
    }

    return this.validate(user, requiredFields);
  }

  private validateAddress(address?: Address): boolean {
    const requiredFields = ['address_1', 'address_2', 'postcode', 'municipality', 'city', 'region', 'country'];
    if (!address) return true;
    return this.validate(address, requiredFields);
  }

  private validate(entity: User | Address, requiredFields: string[]): boolean {
    for (const field of requiredFields) {
      if (!entity[field]) {
        return true;
      }
    }
    return false;
  }

  public validateDateOfBirth(dateOfBirth: string | Date): boolean {
    if(!dateOfBirth) return true;
    const age = moment().diff(dateOfBirth, 'years');
    return age < 18;
  }

  private parseValidationErrors(errorResponse: any) {
    const error = errorResponse.error;
    if (+error.code === 422 && error.errors) {
      for (const [key, value] of Object.entries(error.errors)) {
        const valueArray = value as any;
        valueArray.forEach(e => this.toastr.error(e));
      }
    }
  }

  public validIdCards(clientCase: Case): boolean {
    return clientCase.joint_application ?
      !! (clientCase.client?.id_card && clientCase.partner?.id_card) :
      !! (clientCase.client?.id_card);
  }

  private buildPaymentPlanForm(casePaymentPlan: CasePaymentPlan): void {
    this.paymentPlanForm = this.fb.group({
      phase_one_value:       [casePaymentPlan.phase_one_value, [Validators.min(50)]],
      phase_one_monthly_fee: [casePaymentPlan.phase_one_monthly_fee],
      phase_one_duration:    [{value: casePaymentPlan.phase_one_duration, disabled: false}, [Validators.min(1)]],
      phase_two_value:       [{
        value:    casePaymentPlan.phase_two_value,
        disabled: this.case.product.slug === 'unified'
      }, []],
      phase_two_monthly_fee: [
        {
          value:    casePaymentPlan.phase_two_monthly_fee,
          disabled: this.case.product.slug === 'unified'
        }
      ],
      phase_two_duration:    [
        {
          value:    casePaymentPlan.phase_two_duration,
          disabled: this.case.product.slug === 'unified'
        }, []
      ],
      phase_two_defer:       [casePaymentPlan.phase_two_defer]
    });
    this.subscribeToPaymentPlanChanges();
  }

  private buildPaymentConfigurationForm(paymentConfig: CasePaymentPlan) {
    this.paymentConfigurationForm = this.fb.group({
      initial_fee:       [
        paymentConfig.initial_fee === 0 ? null : paymentConfig.initial_fee,
        [Validators.min(1), Validators.max(5000)]],
      payment_method_id: [paymentConfig.payment_method_id],
      iban:              [paymentConfig.iban],
      payment_day:       [paymentConfig.payment_day, [Validators.min(1), Validators.max(28)]],
      contract_date:     [paymentConfig.contract_date],
      phase2_start_from: [paymentConfig.phase2_start_from],
      charge_amount:     [paymentConfig.charge_amount, [Validators.min(1), Validators.max(5000)]]
    });
  }

  public submitPaymentPlanForm(paymentPlanForm: UntypedFormGroup) {
    if (paymentPlanForm.invalid) {
      paymentPlanForm.markAllAsTouched();
      return;
    }
    this.submittingPaymentPlan = true;
    this.casePaymentPlanService.upsertPaymentPlan(this.case.id, paymentPlanForm.getRawValue())
      .pipe(finalize(() => this.submittingPaymentPlan = false))
      .subscribe(
        () => {
          this.paymentPlanForm.markAsPristine();
          this.toastr.success(this.translate.instant('CASES.editor.payment.payment_plan.result.success'));
        },
        error => {
          this.paymentPlanResponse = error.error;
          this.toastr.error(this.translate.instant('CASES.editor.payment.payment_plan.result.error'),
            error.error.message);
        }
      );
  }

  public recalculatePaymentPlanForm(): void {
    this.recalculatePaymentPlan = true;
    this.casePaymentPlanService.recalculatePaymentPlan(this.case.id, {})
      .pipe(finalize(() => this.recalculatePaymentPlan = false))
      .subscribe(
        (result) => {
          const data = result.data;

          this.paymentPlanForm.patchValue({
            phase_one_value: data.phase_one_value,
            phase_one_monthly_fee: data.phase_one_monthly_fee,
            phase_one_duration: data.phase_one_duration,
            phase_two_value: data.phase_two_value,
            phase_two_monthly_fee: data.phase_two_monthly_fee,
            phase_two_duration: data.phase_two_duration,
            phase_two_defer: data.phase_two_defer
          }, {emitEvent: false});

          this.paymentPlanForm.updateValueAndValidity();
          this.paymentPlanForm.markAsPristine();
          this.toastr.success(this.translate.instant('SHARED.success'));
        },
        error => {
          this.paymentPlanResponse = error.error;
          this.toastr.error(this.translate.instant('SHARED.error'), error.error.message);
        }
      );
  }


  public submitPaymentConfigurationForm(paymentConfigurationForm: UntypedFormGroup) {
    if (paymentConfigurationForm.invalid) {
      paymentConfigurationForm.markAllAsTouched();
      return;
    }
    this.submittingPaymentConfiguration = true;
    this.casePaymentPlanService.upsertPaymentConfig(this.case.id, paymentConfigurationForm.getRawValue())
      .pipe(finalize(() => this.submittingPaymentConfiguration = false))
      .subscribe(
        () => {
          this.paymentConfigurationForm.markAsPristine();
          this.reloadCase(this.case.id);
          this.toastr.success(this.translate.instant('CASES.editor.payment.payment_config.result.success'));
        },
        error => {
          this.paymentConfigResponse = error.error;
          this.toastr.error(
            this.translate.instant('CASES.editor.payment.payment_config.result.error'), error.error.message
          );
        }
      );
  }

  public updateUserIdCard(idCard: string, relation: 'client' | 'partner') {
    this.case[relation].id_card = idCard;
  }

  public updatedClient(newUser: User, relation: 'client' | 'partner') {
    this.case[relation] = newUser;
  }

  public displayDistributionWarningModal(): void {
    if (this.case.distribution && this.case.distribution.distribution_batch_id) {
      Swal.fire({
        text:               this.translate.instant('DISTRIBUTION.modals.prior-authorization'),
        icon:               'warning',
        showCancelButton:   false,
        confirmButtonText:  'OK',
        confirmButtonColor: '#4267b2'
      });
    } else if (this.case.distribution && Object.keys(this.case.distribution).length > 0) {
      Swal.fire({
        text:               this.translate.instant('DISTRIBUTION.modals.notify-of-changes'),
        icon:               'warning',
        showCancelButton:   false,
        confirmButtonText:  'OK',
        confirmButtonColor: '#4267b2'
      });
    }
  }

  public isAllowedProduct(product: Product): boolean {
    if (product.group_slug === 'unified' || product.group_slug === 'dmp' || product.group_slug === 'ds') {
      return false;
    }
    this.productNotAllowed = this.translate.instant('CASES.single.product-not-allowed');
    return true;
  }
}
